import './styles.scss'

import { useTranslation } from 'react-i18next'
import { CheckCircleFilled } from '@ant-design/icons'
import { Form, Modal } from 'antd'

import CustomButton from '@/components/CustomButton'
import { fapiCustomerService } from '@/services/fapi'
import useAsync from '@/hooks/useAsync'

import { EnquiryForm } from './RequestInfoForm'

const { useForm } = Form

interface RequestInfoModalProps {
  onClose: () => void
  serviceName: string
  companyName?: string
  phoneNumber?: string
}

const RequestInfoModal = ({
  onClose,
  companyName = 'N/A',
  phoneNumber = '',
  serviceName,
}: RequestInfoModalProps) => {
  const { t } = useTranslation()
  const [form] = useForm()

  const submitRequestInformation = async (parameters) =>
    fapiCustomerService.submitRequestInformation(parameters)

  const { execute, status, error } = useAsync(submitRequestInformation, false)

  const isSuccess = status === 'success'

  const isLoading = status === 'pending'

  const shouldShowInput = !isSuccess

  const servicePackageName = t(`service.servicePackages.${serviceName}.title`)

  const handleRequestInformation = async ({
    contactType: type,
    ...parameters
  }) => {
    const body = { ...parameters }
    if (type === 'contactEmail') {
      delete body.contactPhone
    } else if (type === 'contactPhone') {
      delete body.contactEmail
    }

    execute(body)
  }

  const handleSubmit = async () => {
    try {
      await form.validateFields()
      const values = form.getFieldsValue()

      const bodyRequest = {
        servicePackageName,
        ...values,
      }

      handleRequestInformation(bodyRequest)
    } catch (error) {
      // Handle validation error
      console.error('Validation failed:', error)
      return
    }

    return {}
  }

  return (
    <Modal
      open
      className="enquiry-modal"
      closable={false}
      footer={null}
      width={570}
    >
      <h3 className="enquiry-modal__header">{servicePackageName}</h3>
      {isSuccess
        ? (
          <div className="enquiry-modal-success">
            <CheckCircleFilled />
            <span className="enquiry-modal-success__message">
              {t('service.servicePackages.requestInformation.successfullMessage')}
            </span>
            <CustomButton onClick={onClose}>
              {t(
                'service.servicePackages.requestInformation.backToServicePackages',
              )}
            </CustomButton>
          </div>
        )
        : null}
      {shouldShowInput
        ? (
          <>
            <div className="enquiry-modal__description">
              {t('service.servicePackages.requestInformation.description')}
            </div>

            {error
              ? (
                <div className="enquiry-modal-error">
                  {t('service.servicePackages.requestInformation.errorMessage')}
                </div>
              )
              : null}

            <EnquiryForm
              form={form}
              initialValues={{ companyName, contactPhone: phoneNumber }}
              isLoading={isLoading}
            />

            <div className="enquiry-modal__footer">
              <CustomButton disabled={isLoading} type="ghost" onClick={onClose}>
                {t('enquiryModal.button.cancel')}
              </CustomButton>
              <CustomButton loading={isLoading} onClick={handleSubmit}>
                {t('enquiryModal.button.submitForm')}
              </CustomButton>
            </div>
          </>
        )
        : null}
    </Modal>
  )
}

export default RequestInfoModal
